import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import {
    EmailDataFormParameters,
    FailureResponse,
    SplunkReportConfig,
} from 'store/auth/actions/auth-actions/auth-action-types';
import { emailRules } from 'features/authentication/signup/validations/validations';
import { useForm } from 'react-hook-form';
import {
    BACKEND_DEFAULT_CODE,
    BACKEND_INACTIVE_ACCOUNT_ERROR_CODE,
    BACKEND_RECAPTCHA_ERROR_CODE,
    BACKEND_SUCCESS_CODE,
    GOOGLE_RECAPTCHA_SITE_KEY,
} from 'features/authentication/utils/constants';
import { Button } from '@placer-ui/components';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSplunkCallback } from 'hooks/use-splunk-callback/use-splunk-callback';

import styles from './send-email-input-form.module.scss';
import { ControlledInputField } from 'components/controlled-input-field';

type SendEmailInputFormProps = {
    sendEmail: (data: EmailDataFormParameters) => Promise<number>; // return status number
    isRecaptchaEnabled?: boolean;
    onSuccessCallback: (value: string) => void;
    onFailureCallback?: (value: string, splunkReportConfig: SplunkReportConfig) => void;
    inactiveAccountSplunkEventParams?: Object;
    email?: string;
    submitButtonLabel: string;
    setParentErrorCode?: (errorCode: number) => void;
    inputTestId?: string;
    submitEmailButtonTestId?: string;
};

export const SendEmailInputForm = ({
    onSuccessCallback,
    onFailureCallback,
    sendEmail,
    isRecaptchaEnabled = true,
    inactiveAccountSplunkEventParams = {},
    email = '',
    submitButtonLabel,
    setParentErrorCode,
    inputTestId = 'email-input-field',
    submitEmailButtonTestId = 'submit-email-button',
}: SendEmailInputFormProps) => {
    const [showRecaptcha, setShowRecaptcha] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState(BACKEND_DEFAULT_CODE);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
    const sendEvent = useSplunkCallback();

    const { control, handleSubmit } = useForm<EmailDataFormParameters>({
        defaultValues: {
            email,
            recaptcha_response: recaptchaToken || '',
        },
        mode: 'onChange',
    });

    useEffect(() => {
        if (errorCode === BACKEND_RECAPTCHA_ERROR_CODE) {
            recaptchaRef.current?.reset();
            setErrorCode(BACKEND_DEFAULT_CODE);
        }
    }, [errorCode]);

    const onRecaptchaChange = (token: string | null) => {
        setRecaptchaToken(token);
        setSubmitButtonDisabled(false);
    };

    const onRecaptchaExpired = () => {
        setRecaptchaToken(null);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.charCode === 13) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        }
    };

    const onFailure = (email: string, errorCode: number, error?: string) => {
        onFailureCallback?.(email, {
            statusCode: errorCode,
            error,
        });
    };

    const onSubmit = async (formSubmissionValues: EmailDataFormParameters) => {
        setParentErrorCode?.(0);
        if (isRecaptchaEnabled) setShowRecaptcha(true);

        setErrorCode(BACKEND_DEFAULT_CODE);
        setSubmitButtonDisabled(false);
        const emailValue = formSubmissionValues.email;
        if (emailValue && (!isRecaptchaEnabled || recaptchaToken)) {
            setSubmitButtonDisabled(true);
            try {
                const sendEmailStatus: number = await sendEmail(formSubmissionValues);
                setErrorCode(sendEmailStatus);
                if (sendEmailStatus === BACKEND_SUCCESS_CODE) {
                    onSuccessCallback(emailValue);
                } else if (sendEmailStatus === BACKEND_INACTIVE_ACCOUNT_ERROR_CODE) {
                    setSubmitButtonDisabled(false);
                    onFailure(emailValue, sendEmailStatus);
                    inactiveAccountSplunkEventParams && sendEvent(inactiveAccountSplunkEventParams);
                }
            } catch (err: any) {
                const { error_code: statusCode, error }: FailureResponse = await err.json();
                setSubmitButtonDisabled(false);
                onFailure(emailValue, statusCode, error);
                setErrorCode(statusCode);
            }
        }
    };

    return (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <ControlledInputField<EmailDataFormParameters>
                name="email"
                placeholder="name@company.com"
                control={control}
                rules={emailRules}
                onKeyPress={handleKeyPress}
                testId={inputTestId}
                label={'Email Address'}
                autoComplete="email"
                asterix
                required
            />
            {showRecaptcha && (
                <ReCAPTCHA
                    sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    ref={recaptchaRef}
                />
            )}
            <Button
                variant="primary-brand"
                fullWidth
                disabled={submitButtonDisabled}
                testId={submitEmailButtonTestId}
                type="submit"
            >
                {submitButtonLabel}
            </Button>
        </form>
    );
};
